// Cumbre como guía del onboarding de marca (ADR-2026-10-01-onboarding-v2-cumbre-guia).
//
// Arriba a la izquierda, con un globo que le sale de la cara: dice el saludo, la pregunta
// y el detalle, letra por letra, y después sigue hablando para reaccionar a lo que hace
// la persona. La pantalla decide QUÉ dice (`lib/onboardingMarca/dialogo.ts`) y cómo está
// de ánimo; acá sólo se anima.
//
// El globo se dimensiona con el texto ENTERO desde el arranque —lo que falta escribir va
// transparente—, así las palabras no saltan de renglón mientras aparecen y el formulario de
// abajo no se mueve.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CumbreMascot } from '../../CumbreMascot';
import {
  demoraAntesDe,
  demoraDespuesDe,
  letrasTotales,
  reparto,
  type Reaccion,
  type Tramo,
} from '../../../lib/onboardingMarca/dialogo';
import './guiaCumbre.css';

/** Cómo está Cumbre cuando no está hablando. Mientras escribe, siempre habla. */
export type Animo = 'escucha' | 'mira' | 'piensa';

/** Un gesto de una vez: `n` cambia para repetirlo. */
export interface Gesto {
  tipo: 'festeja' | 'duda';
  n: number;
}

/**
 * El diálogo letra por letra. `instantaneo` lo da por escrito (pide menos movimiento).
 * Cuando cambian los `tramos` (otra pregunta) vuelve a empezar, sin desmontar a Cumbre:
 * por eso la pantalla tiene que pasarlos memorizados.
 */
export function useDialogo(tramos: Tramo[], { instantaneo, arrancarEn }: { instantaneo: boolean; arrancarEn: number }) {
  const total = letrasTotales(tramos);
  const [letras, setLetras] = useState(instantaneo ? total : 0);
  const [arranco, setArranco] = useState(instantaneo);
  const [deQue, setDeQue] = useState(tramos);
  if (deQue !== tramos) {
    // Reinicio durante el render (el patrón de React para "estado derivado de una prop"):
    // con un efecto se vería un cuadro con el texto nuevo entero antes de borrarse.
    setDeQue(tramos);
    setLetras(instantaneo ? total : 0);
    setArranco(instantaneo);
  }

  useEffect(() => {
    if (arranco) return;
    const t = window.setTimeout(() => setArranco(true), arrancarEn);
    return () => window.clearTimeout(t);
  }, [arranco, arrancarEn]);

  useEffect(() => {
    if (!arranco || letras >= total) return;
    const t = window.setTimeout(() => setLetras((n) => n + 1), demoraAntesDe(tramos, letras));
    return () => window.clearTimeout(t);
  }, [arranco, letras, total, tramos]);

  const completar = useCallback(() => {
    setArranco(true);
    setLetras(total);
  }, [total]);

  return { letras, terminado: letras >= total, hablando: arranco && letras < total, completar };
}

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Escribe una reacción corta; vuelve a empezar cada vez que cambia su `id`. Sale escrita
 * de una sólo si la persona pide menos movimiento.
 */
function useReaccion(reaccion: Reaccion | null) {
  const [instantaneo] = useState(pideMenosMovimiento);
  const texto = reaccion?.texto ?? '';
  const largo = texto.length;
  const [letras, setLetras] = useState(0);

  useEffect(() => {
    setLetras(instantaneo ? largo : 0);
    // Sólo el id: el mismo texto no se reescribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reaccion?.id]);

  useEffect(() => {
    if (letras >= largo) return;
    const demora = letras === 0 ? 140 : Math.round(demoraDespuesDe(texto[letras - 1]) * 0.8);
    const t = window.setTimeout(() => setLetras((n) => n + 1), demora);
    return () => window.clearTimeout(t);
  }, [texto, letras, largo]);

  return { letras, hablando: letras < largo };
}

/** Un texto a medio escribir. Los lectores de pantalla leen el entero, una vez. */
function Escrito({ texto, visibles, cursor }: { texto: string; visibles: number; cursor: boolean }) {
  const hecho = texto.slice(0, Math.max(0, visibles - 1));
  const ultima = visibles > 0 ? texto[visibles - 1] : '';
  return (
    <>
      <span className="sr-only">{texto}</span>
      <span aria-hidden="true">
        {hecho}
        {/* Sólo la última letra se anima: una por span sería caro y no se ve distinto. */}
        {ultima && (
          <span key={visibles} className="om-letra">
            {ultima}
          </span>
        )}
        {cursor && <span className="om-cursor" />}
        <span className="om-falta">{texto.slice(visibles)}</span>
      </span>
    </>
  );
}

const CLASE_DE_TRAMO: Record<Tramo['estilo'], string> = {
  saludo: 'om-globo__saludo',
  pregunta: 'om-globo__pregunta',
  detalle: 'om-globo__detalle',
};

const CHISPAS = [0, 1, 2, 3, 4, 5];

export function GuiaCumbre({
  tramos,
  letras,
  hablando,
  terminado,
  onCompletar,
  reaccion,
  junto,
  animo,
  gesto,
  instantaneo,
}: {
  tramos: Tramo[];
  letras: number;
  hablando: boolean;
  terminado: boolean;
  onCompletar: () => void;
  /** Reemplaza al detalle mientras no sea null. */
  reaccion: Reaccion | null;
  /** Algo que va debajo del saludo, al lado de Cumbre (sólo en el teléfono, donde el saludo
   *  vive afuera del globo). Ej: cómo va la lectura de la web en las preguntas. */
  junto?: ReactNode;
  animo: Animo;
  gesto: Gesto | null;
  instantaneo: boolean;
}) {
  const r = useReaccion(terminado ? reaccion : null);
  const habla = hablando || r.hablando;
  const visibles = reparto(tramos, letras);
  const enCurso = hablando ? visibles.findIndex((v, i) => v < tramos[i].texto.length) : -1;

  // Con el diálogo entero el globo toma su alto final; las reacciones son más cortas y
  // no lo achican (el formulario de abajo no salta).
  // El alto se mide por diálogo: otra pregunta trae otro texto y su propio alto.
  const globoRef = useRef<HTMLDivElement>(null);
  const [medido, setMedido] = useState<{ de: Tramo[]; alto: number } | null>(null);
  const altoMinimo = medido?.de === tramos ? medido.alto : undefined;
  useLayoutEffect(() => {
    if (terminado && altoMinimo === undefined && globoRef.current) {
      setMedido({ de: tramos, alto: globoRef.current.offsetHeight });
    }
  }, [terminado, altoMinimo, tramos]);
  // Una clave por diálogo: el contenido del globo entra con un fundido al cambiar.
  const claveDelDialogo = tramos.map((t) => t.texto).join('|');

  const estado = habla ? 'answering' : animo === 'piensa' ? 'thinking' : 'idle';
  const mira = !habla && animo === 'mira';

  // Enter o Escape completan el diálogo: con el teclado no hay globo que tocar.
  useEffect(() => {
    if (terminado) return;
    const alTeclear = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') onCompletar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [terminado, onCompletar]);

  return (
    <div className={`om-guia ${instantaneo ? 'om-guia--quieta' : ''}`}>
      <div className="om-guia__cabeza">
        <div className="om-cumbre" data-mira={mira ? 'abajo' : undefined}>
          <span key={gesto ? `${gesto.tipo}-${gesto.n}` : 'quieta'} className={`om-cumbre__gesto om-gesto--${gesto ? gesto.tipo : 'saluda'}`}>
            <span className={`om-cumbre__habla ${habla ? 'is-hablando' : ''}`}>
              <CumbreMascot state={estado} size={88} decorativa className="om-cumbre__svg" />
            </span>
          </span>
          {gesto?.tipo === 'festeja' && (
            <span key={`chispas-${gesto.n}`} className="om-chispas" aria-hidden="true">
              {CHISPAS.map((i) => (
                <span key={i} className="om-chispa" style={{ ['--i' as string]: i }} />
              ))}
            </span>
          )}
        </div>
        {/* En el teléfono el saludo se dice al lado de Cumbre, fuera del globo: ocupa el
            lugar que quedaba vacío a su derecha. Se escribe en los dos lugares y el CSS
            muestra uno (`display: none` también lo saca del árbol de accesibilidad). */}
        <div className="om-guia__voz">
          {tramos.map((t, i) =>
            t.estilo === 'saludo' ? (
              <p key={`${claveDelDialogo}-${i}`} className="om-guia__saludo" onClick={terminado ? undefined : onCompletar}>
                <Escrito texto={t.texto} visibles={visibles[i]} cursor={i === enCurso} />
              </p>
            ) : null,
          )}
          {junto && <div className="om-guia__junto">{junto}</div>}
        </div>
      </div>

      <div
        ref={globoRef}
        className={`om-globo ${terminado ? '' : 'is-saltable'}`}
        style={altoMinimo ? { minHeight: altoMinimo } : undefined}
        // Tocar el globo lo completa. No es un botón: un `role="button"` le escondería la
        // pregunta a los lectores de pantalla; con el teclado van Enter o Escape.
        onClick={terminado ? undefined : onCompletar}
      >
        <div key={claveDelDialogo} className="om-globo__contenido">
        {tramos.map((t, i) => {
          if (t.estilo === 'detalle' && terminado && reaccion) {
            return (
              <p key={`r-${reaccion.id}`} className={`${CLASE_DE_TRAMO.detalle} om-globo__reaccion`}>
                <Escrito texto={reaccion.texto} visibles={r.letras} cursor={r.hablando} />
              </p>
            );
          }
          const Tag = t.estilo === 'pregunta' ? 'h1' : 'p';
          return (
            <Tag key={i} className={CLASE_DE_TRAMO[t.estilo]}>
              <Escrito texto={t.texto} visibles={visibles[i]} cursor={i === enCurso} />
            </Tag>
          );
        })}
        </div>
      </div>

      {/* Las reacciones llegan después de leído el globo: se anuncian aparte. */}
      <p className="sr-only" aria-live="polite">
        {terminado && reaccion ? reaccion.texto : ''}
      </p>
    </div>
  );
}

export default GuiaCumbre;
